import { useState, useEffect, useRef } from "react";
import { Search, X, Clock, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BlogPost } from "@/hooks/useBlogPosts";

interface BlogSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  posts: BlogPost[];
  onPostSelect: (post: BlogPost) => void;
}

const SEARCH_HISTORY_KEY = 'blog_search_history';
const MAX_HISTORY_ITEMS = 5;

export const BlogSearchBar = ({ value, onChange, posts, onPostSelect }: BlogSearchBarProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load search history from localStorage
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  useEffect(() => {
    // Click outside to close suggestions
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addToHistory = (query: string) => {
    if (!query.trim()) return;
    
    const newHistory = [query, ...searchHistory.filter(h => h !== query)].slice(0, MAX_HISTORY_ITEMS);
    setSearchHistory(newHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    setShowSuggestions(true);
  };

  const handlePostClick = (post: BlogPost) => {
    addToHistory(post.title);
    onChange('');
    setShowSuggestions(false);
    onPostSelect(post);
  };

  const handleHistoryClick = (query: string) => {
    onChange(query);
    setShowSuggestions(false);
  };

  // Filter posts based on search query
  const suggestions = value.trim() 
    ? posts.filter(post => 
        post.title.toLowerCase().includes(value.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(value.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(value.toLowerCase())) ||
        post.category.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5)
    : [];

  const showHistorySection = !value.trim() && searchHistory.length > 0;
  const showSuggestionsSection = value.trim() && suggestions.length > 0;

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl mx-auto">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          type="text"
          aria-label="Search articles"
          placeholder="Search articles by title, topic, or tag..."
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          className="pl-10 pr-10 h-12 text-base"
        />
        {value && (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Clear article search"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
            onClick={() => {
              onChange('');
              setShowSuggestions(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {showSuggestions && (showHistorySection || showSuggestionsSection) && (
        <Card className="absolute z-50 w-full mt-2 max-h-96 overflow-y-auto shadow-lg">
          {/* Search History */}
          {showHistorySection && (
            <div className="p-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4" />
                  Recent Searches
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearHistory}
                  className="h-6 text-xs"
                >
                  Clear
                </Button>
              </div>
              <div className="space-y-1">
                {searchHistory.map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleHistoryClick(query)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm"
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Autocomplete Suggestions */}
          {showSuggestionsSection && (
            <div className="p-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <TrendingUp className="h-4 w-4" />
                Suggested Articles
              </div>
              <div className="space-y-2">
                {suggestions.map((post) => (
                  <button
                    key={post.id}
                    onClick={() => handlePostClick(post)}
                    className="w-full text-left p-3 rounded-lg hover:bg-muted transition-colors group"
                  >
                    <div className="flex gap-3">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm line-clamp-1 group-hover:text-primary transition-colors">
                          {post.title}
                        </h4>
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {post.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {post.readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {value.trim() && suggestions.length === 0 && (
            <div className="p-6 text-center text-muted-foreground text-sm">
              No articles found matching "{value}"
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
